import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Pet, PetData } from '../types/pets';
import PetForm from '../components/PetForm';
import './ManagePets.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

// Helper function to calculate calories
function calculateCalories(pet: Partial<Pet>): number {
  if (!pet.weight || !pet.life_stage || !pet.activity_level || !pet.body_condition) {
    return 0;
  }

  let RER = 70 * Math.pow(pet.weight, 0.75);
  
  // Activity level multiplier
  let multiplier = 1.0;
  switch (pet.activity_level) {
    case 'lazyBones':
      multiplier = 1.2;
      break;
    case 'typical':
      multiplier = 1.4;
      break;
    case 'active':
      multiplier = 1.6;
      break;
  }

  // Life stage adjustment
  if (pet.life_stage === 'puppy') {
    multiplier *= 2;
  } else if (pet.life_stage === 'senior') {
    multiplier *= 0.8;
  }

  // Body condition adjustment
  if (pet.body_condition === 'overweight') {
    multiplier *= 0.8;
  } else if (pet.body_condition === 'underweight') {
    multiplier *= 1.2;
  }

  return Math.round(RER * multiplier);
}

const ManagePets: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [petData, setPetData] = useState<PetData>({ pets: [] });
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [requestingLink, setRequestingLink] = useState(false);
  const [email, setEmail] = useState('');

  // Add/Edit form state
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingPet, setEditingPet] = useState<Pet | null>(null);

  const handleAddPet = () => {
    setEditingPet(null); // Clear any existing pet data
    setIsFormOpen(true);
  };

  const handleEditPet = (petId: string) => {
    const pet = petData?.pets.find(p => p.id === petId);
    if (pet) {
      setEditingPet(pet);
      setIsFormOpen(true);
    }
  };

  const handleDeletePet = async (petId: string) => {
    if (!authToken) return;

    try {
      if (!window.confirm('Are you sure you want to delete this furbaby? This will not change any active subscriptions.')) {
        return;
      }

      const updatedPets: PetData = {
        pets: petData.pets.filter((pet: Pet) => pet.id !== petId)
      };

      console.log('Sending delete request with token:', authToken);
      
      const response = await fetch(`${API_URL}/api/metafields`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          customer_id: JSON.parse(atob(authToken.split('.')[1])).customerId,
          metafield: {
            namespace: 'custom',
            key: 'pet_data',
            value: updatedPets,
            type: 'json'
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Server error:', {
          status: response.status,
          statusText: response.statusText,
          error: errorData
        });
        throw new Error(errorData.error || 'Failed to delete pet');
      }

      setPetData(updatedPets);

    } catch (err) {
      console.error('Error deleting pet:', err);
      alert('Failed to delete pet. Please try again.');
    }
  };

  const handleSavePet = async (newPetData: Partial<Pet>) => {
    if (!authToken || !newPetData) return;

    try {
      let photoUrl: string | undefined = undefined;
      const photoInput = document.getElementById('pet-photo-upload') as HTMLInputElement;
      
      if (photoInput.files && photoInput.files[0]) {
        const formData = new FormData();
        formData.append('file', photoInput.files[0]);

        const uploadResponse = await fetch(`${API_URL}/api/upload-photo`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authToken}`,
          },
          credentials: 'include',
          body: formData
        });

        if (!uploadResponse.ok) {
          throw new Error('Failed to upload photo');
        }

        const uploadResult = await uploadResponse.json();
        console.log('Photo upload response:', uploadResult);
        
        // The server returns { url: string } directly
        photoUrl = uploadResult.url;
      }

      let updatedPets: PetData;

      if (editingPet) {
        // Update existing pet
        updatedPets = {
          pets: petData.pets.map((pet: Pet) => 
            pet.id === editingPet.id 
              ? { 
                  ...pet, 
                  ...newPetData,
                  includeInFamilyFeedingPlan: newPetData.includeInFamilyFeedingPlan ?? pet.includeInFamilyFeedingPlan,
                  photo_url: photoUrl || pet.photo_url // Keep existing photo if no new one
                } 
              : pet
          )
        };
      } else {
        // Add new pet
        const newPet: Pet = {
          ...newPetData as Pet,
          id: `dog_${Date.now()}`,
          calories_per_day: calculateCalories(newPetData),
          includeInFamilyFeedingPlan: newPetData.includeInFamilyFeedingPlan ?? true,
          photo_url: photoUrl
        };
        updatedPets = {
          pets: [...petData.pets, newPet]
        };
      }

      console.log('Saving pet data:', updatedPets);

      // Post the entire pets metafield
      const response = await fetch(`${API_URL}/api/metafields`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          customer_id: JSON.parse(atob(authToken.split('.')[1])).customerId,
          metafield: {
            namespace: 'custom',
            key: 'pet_data',
            value: updatedPets,
            type: 'json'
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Server error:', errorData);
        throw new Error(errorData.error || 'Failed to update pets');
      }

      setPetData(updatedPets);
      setIsFormOpen(false);
      setEditingPet(null);

    } catch (err) {
      console.error('Error saving pet:', err);
      alert(`Failed to ${editingPet ? 'update' : 'add'} pet. Please try again.`);
    }
  };

  const fetchPets = async (token: string) => {
    try {
      const tokenPayload = JSON.parse(atob(token.split('.')[1]));
      console.log('Token payload:', tokenPayload);
      
      // Try fetching metafields directly first
      console.log('Fetching metafields for customer:', tokenPayload.customerId);
      const metaResponse = await fetch(`${API_URL}/api/metafields`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          customer_id: tokenPayload.customerId,
          metafield: {
            namespace: 'custom',
            key: 'pet_data'
          }
        })
      });
      
      if (metaResponse.ok) {
        const metaData = await metaResponse.json();
        console.log('Raw metafield data:', metaData);
        
        // Parse the nested JSON string in metafield.value
        if (metaData.metafield && metaData.metafield.value) {
          const petData = JSON.parse(metaData.metafield.value);
          console.log('Parsed pet data:', petData);
          setPetData(petData);
          return;
        }
      }

      // Fallback to pets endpoint
      const response = await fetch(`${API_URL}/api/pets`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Pets endpoint data:', data);
        setPetData(data);
      } else {
        const errorData = await response.json();
        console.error('Error response:', errorData);
        setError('Failed to fetch pets');
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setError('Failed to fetch pets');
    }
  };

  const handleRequestMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const webhookUrl = process.env.REACT_APP_REQUEST_MAGIC_LINK_WEBHOOK_URL;
      if (!webhookUrl) {
        throw new Error('Webhook URL not configured');
      }

      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      if (response.ok) {
        setRequestingLink(false);
        setError('Magic link requested. If you are a valid My Furbaby customer, you should receive a magic link by email or SMS shortly.');
      } else {
        setError('Failed to send magic link. Please try again.');
      }
    } catch (err) {
      setError('Failed to send magic link. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const checkAuth = async () => {
      setLoading(true);
      const magicToken = searchParams.get('token');

      try {
        // If there's a magic token in URL, try to use it first
        if (magicToken) {
          const response = await fetch(`${API_URL}/api/auth/verify`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ token: magicToken })
          });

          if (response.ok) {
            const data = await response.json();
            setAuthToken(data.token);
            setIsAuthenticated(true);
            fetchPets(data.token);
            return;
          }
        }

        // If no magic token or it failed, try session token
        const response = await fetch(`${API_URL}/api/auth/check`, {
          credentials: 'include'
        });

        if (response.ok) {
          const data = await response.json();
          setAuthToken(data.token);
          setIsAuthenticated(true);
          fetchPets(data.token);
        } else {
          setIsAuthenticated(false);
          setAuthToken(null);
        }
      } catch (err) {
        setError('Authentication failed');
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [searchParams]);

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return (
      <div className="auth-container">
        <h1>My Furbaby Pet Management</h1>
        {requestingLink ? (
          <form onSubmit={handleRequestMagicLink} className="magic-link-form">
            <p>Enter your email to receive a magic link:</p>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
            />
            <button type="submit">Send Magic Link</button>
            <button type="button" onClick={() => setRequestingLink(false)}>Cancel</button>
          </form>
        ) : (
          <div>
            <p>Please use a valid magic link to access this page.</p>
            <button onClick={() => setRequestingLink(true)}>Request Magic Link</button>
          </div>
        )}
        {error && <div className="error-message">{error}</div>}
      </div>
    );
  }

  return (
    <div className="manage-pets">
      <h1>Manage My Furbabies</h1>
      
      <div className="pets-list">
        {petData && petData.pets && petData.pets.map((pet) => (
          <div key={pet.id} className="pet-card">
            <div className="pet-photo">
              {pet.photo_url ? (
                <img src={pet.photo_url} alt={pet.name} />
              ) : (
                <div className="photo-placeholder">Add Photo</div>
              )}
            </div>
            
            <div className="pet-info">
              <h2>{pet.name}{!pet.includeInFamilyFeedingPlan && ' (Unsubscribed)'}</h2>
              <div className="pet-details">
                <p>Age: {pet.life_stage}</p>
                <p>Weight: {pet.weight} kg</p>
                <p>Lifestyle: {pet.activity_level}</p>
                <p>Breed: {pet.breed}</p>
                <p>{pet.calories_per_day} calories per day</p>
                <p>{pet.percent_kibble}% FMFB</p>
              </div>

              <div className="feeding-stats">
                <div>
                  <span>{pet.calories_per_day}</span>
                  <span>calories/day</span>
                </div>
                <div>
                  <span>{Math.round(pet.calories_per_day / 3.28)}</span>
                  <span>g/day</span>
                </div>
                <div>
                  <span>{((pet.calories_per_day / 3.28) / 110).toFixed(1)}</span>
                  <span>cups/day</span>
                </div>
              </div>

              <div className="pet-actions">
                <button onClick={() => handleEditPet(pet.id)}>Edit Details</button>
                <button onClick={() => handleDeletePet(pet.id)}>Delete Furbaby</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <button className="add-pet-button" onClick={handleAddPet}>
        Add Pet
      </button>

      {/* We'll need to create this PetForm component next */}
      {isFormOpen && (
        <PetForm
          pet={editingPet}
          onSave={handleSavePet}
          onCancel={() => {
            setIsFormOpen(false);
            setEditingPet(null);
          }}
        />
      )}
    </div>
  );
};

export default ManagePets;
